import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import type { ExtendTarget } from '../App';
import { api, type Subscription, type SubscriptionConfig } from '../api/client';
import { T } from '../styles/tokens';
import { haptic, hapticSuccess, tg } from '../telegram/sdk';

interface Props {
  onBuy: () => void;
  onExtend: (target: ExtendTarget) => void;
}

export default function Dashboard({ onBuy, onExtend }: Props) {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.subs.list().then(setSubs).catch(console.error).finally(() => setLoading(false));
  }, []);

  const active = subs.filter((s) => s.status === 'active');
  const minDays = active.reduce((m, s) => (m === -1 ? s.days_left : Math.min(m, s.days_left)), -1);

  return (
    <div style={d.wrap}>
      <div style={d.header}>
        <div style={d.headTag}>// кабинет</div>
        <div style={d.headTitle}>Мои подписки</div>
      </div>

      <div style={d.statsStrip}>
        <Stat val={String(active.length)} label="Активных" />
        <Stat val={minDays >= 0 ? String(minDays) : '—'} label="Дней" />
        <Stat val={String(subs.length)} label="Устройств" />
      </div>

      {loading && <div style={d.empty}>Загрузка...</div>}
      {!loading && subs.length === 0 && (
        <div style={d.empty}>
          <div style={d.emptyTitle}>У вас ещё нет подписок</div>
          <div style={d.emptySub}>Начните с 7-дневного триала</div>
          <button
            style={d.cta}
            onClick={() => {
              haptic('medium');
              onBuy();
            }}
          >
            Выбрать тариф →
          </button>
        </div>
      )}
      {subs.map((sub) => (
        <SubCard key={sub.id} sub={sub} onExtend={onExtend} />
      ))}

      <button
        style={d.newBtn}
        onClick={() => {
          haptic('medium');
          onBuy();
        }}
      >
        + Новая подписка
      </button>
    </div>
  );
}

function Stat({ val, label }: { val: string; label: string }) {
  return (
    <div style={d.stat}>
      <div style={d.statVal}>{val}</div>
      <div style={d.statLabel}>{label}</div>
    </div>
  );
}

function SubCard({ sub, onExtend }: { sub: Subscription; onExtend: (t: ExtendTarget) => void }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<SubscriptionConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isActive = sub.status === 'active';
  const color = sub.protocol === 'wireguard' ? T.accent : T.success;
  const pct = sub.total_days ? Math.min(100, (sub.days_left / sub.total_days) * 100) : 0;

  const loadCfg = async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await api.subs.getConfig(sub.id);
      setCfg(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить конфиг');
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    haptic('light');
    const next = !open;
    setOpen(next);
    if (next && !cfg) await loadCfg();
  };

  const doCopy = async () => {
    if (!cfg) return;
    try {
      await navigator.clipboard.writeText(cfg.link || cfg.content);
      hapticSuccess();
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Не удалось скопировать');
    }
  };

  const [downloading, setDownloading] = useState(false);

  const doDownload = async () => {
    if (!cfg || downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const ticket = await api.subs.downloadTicket(sub.id);
      const w = tg();
      const atLeast = (v: string) =>
        w?.isVersionAtLeast ? w.isVersionAtLeast(v) : false;
      if (w?.downloadFile && atLeast('7.2')) {
        w.downloadFile({ url: ticket.url, file_name: ticket.file_name });
        hapticSuccess();
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } else if (w?.openLink) {
        w.openLink(ticket.url);
        hapticSuccess();
      } else {
        window.open(ticket.url, '_blank');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось скачать файл');
    } finally {
      setDownloading(false);
    }
  };

  const doExtend = () => {
    haptic('medium');
    onExtend({
      subscriptionId: sub.id,
      protocol: sub.protocol,
      deviceLabel: sub.device_label,
    });
  };

  return (
    <div style={{ ...d.sub, borderLeft: `3px solid ${isActive ? color : T.border}`, opacity: isActive ? 1 : 0.6 }}>
      <div style={d.subTag}>{sub.protocol === 'wireguard' ? 'WireGuard' : 'VLESS Reality'}</div>
      <div style={d.subName}>{sub.device_label || `Подписка #${sub.id}`}</div>
      <div style={d.subMeta}>
        {isActive ? `Осталось ${sub.days_left} дн.` : 'Истекла'}
      </div>
      {isActive && (
        <div style={d.progress}>
          <div style={{ ...d.progressFill, width: `${pct}%`, background: color }} />
        </div>
      )}
      {isActive && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button style={d.smBtn} onClick={toggle} disabled={loading}>
            {loading ? '...' : open ? 'Скрыть конфиг' : 'Показать конфиг'}
          </button>
          <button style={d.extBtn} onClick={doExtend}>
            Продлить
          </button>
        </div>
      )}
      {open && (
        <div style={d.configBox}>
          {error && <div style={d.configError}>⚠ {error}</div>}
          {cfg && (
            <>
              <img
                src={`data:image/png;base64,${cfg.qr_png_base64}`}
                alt="QR"
                style={d.qrImg}
              />
              {cfg.protocol === 'wireguard' ? (
                <>
                  <div style={d.hint}>
                    Отсканируйте QR в приложении WireGuard или скачайте файл и откройте его:
                  </div>
                  <button
                    style={d.copyBtn}
                    onClick={doDownload}
                    disabled={downloading}
                  >
                    {downloading
                      ? 'Подготовка...'
                      : copied
                      ? '✓ Готово'
                      : `Скачать ${cfg.filename}`}
                  </button>
                </>
              ) : (
                <>
                  <code style={d.code}>{cfg.content}</code>
                  <button style={d.copyBtn} onClick={doCopy}>
                    {copied ? '✓ Скопировано' : 'Копировать'}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const d: Record<string, CSSProperties> = {
  wrap: { padding: '20px 16px 24px' },
  header: { marginBottom: 18 },
  headTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  headTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em' },
  statsStrip: { display: 'flex', border: `1px solid ${T.border}`, borderRadius: 4, marginBottom: 18, background: T.surface },
  stat: { flex: 1, padding: '14px 10px', borderRight: `1px solid ${T.border}` },
  statVal: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 22, color: T.accent, letterSpacing: '-0.03em' },
  statLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 },
  empty: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: 24, textAlign: 'center' },
  emptyTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16, marginBottom: 6 },
  emptySub: { fontSize: 13, color: T.textSec, marginBottom: 18 },
  sub: { background: T.surface, border: `1px solid ${T.border}`, padding: '16px', marginBottom: 8, borderRadius: 4 },
  subTag: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
  subName: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '-0.02em', marginBottom: 4 },
  subMeta: { fontSize: 12, color: T.textSec },
  progress: { height: 2, background: T.border, borderRadius: 1, marginTop: 10, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 1 },
  smBtn: { flex: 1, background: T.surfaceUp, border: `1px solid ${T.border}`, borderRadius: 3, padding: '7px 12px', color: T.textSec, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  extBtn: { flex: 1, background: T.accentSoft, border: `1px solid ${T.accentMid}`, borderRadius: 3, padding: '7px 12px', color: T.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  configBox: { marginTop: 10, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, padding: 10 },
  code: { display: 'block', fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'oklch(0.75 0.15 30)', lineHeight: 1.6, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const, marginBottom: 8, maxHeight: 200, overflowY: 'auto' as const },
  copyBtn: { width: '100%', background: T.accent, border: 'none', borderRadius: 2, padding: '8px 10px', color: '#000', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  qrImg: { display: 'block', width: 160, height: 160, margin: '0 auto 10px', background: '#fff', padding: 8, borderRadius: 4, imageRendering: 'pixelated' as const },
  configError: { padding: '8px 10px', border: '1px solid oklch(0.58 0.19 27 / 0.5)', background: 'oklch(0.58 0.19 27 / 0.08)', color: 'oklch(0.72 0.19 27)', fontFamily: 'DM Mono, monospace', fontSize: 10, borderRadius: 3, marginBottom: 8 },
  hint: { fontSize: 11, color: T.textSec, lineHeight: 1.5, marginBottom: 10, textAlign: 'center' as const },
  newBtn: { width: '100%', marginTop: 14, background: T.accent, border: 'none', borderRadius: 4, padding: '13px', color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  cta: { marginTop: 12, background: T.accent, border: 'none', borderRadius: 4, padding: '11px 22px', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
};
